import clsx from 'clsx';
import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';
import Offcanvas from 'react-bootstrap/Offcanvas';

import './BayFilterDrawer.scss';

export interface BayFilterDrawerProps {
	handleCloseDrawer: () => void;
	showDrawer: boolean;
	children?: ReactNode;
	headerChildren?: ReactNode;
	footerChildren?: ReactNode;
	title?: string;
	container?: HTMLElement | null;
	styles?: CSSProperties;
	autoFocus?: boolean;
	enforceFocus?: boolean;
	restoreFocus?: boolean;
	isDynamicWidth?: boolean;
	shouldCloseDrawer?: boolean;
	backdrop?: boolean | 'static';
	className?: string;
	/**
	 * disableBannerOffset - boolean - disables the drawer from applying banner offset positioning.
	 * When true -> always uses 'bayfilterdrawer-no-banner' class regardless of actual banner state.
	 * Useful when you need consistent positioning without banner adjustments.
	 */
	disableBannerOffset?: boolean;
	placement?: 'start' | 'end' | 'top' | 'bottom';
	hideDrawerTitle?: boolean;
}

const BayFilterDrawer: React.FC<BayFilterDrawerProps> = ({
	handleCloseDrawer,
	showDrawer,
	children,
	headerChildren,
	footerChildren,
	title,
	container,
	styles,
	autoFocus = true,
	enforceFocus = true,
	restoreFocus = true,
	isDynamicWidth = false,
	shouldCloseDrawer = true,
	backdrop = true,
	className,
	disableBannerOffset = false,
	placement = 'end',
	hideDrawerTitle = false,
}) => {
	const [bannerType, setBannerType] = useState('');

	useEffect(() => {
		// close the drawer if any of the top nav drawers are opened  and when height is less than full view port
		const onClickHandler = (event: MouseEvent) => {
			if ((event.target as HTMLElement).closest('.action-nav')) {
				handleCloseDrawer();
			}
		};

		if (showDrawer) {
			window.addEventListener('click', onClickHandler, true);
			// To check the banner based classes added on bayBody and add class to the offcanvas based on it.
			const bayBodyElement = document.querySelector('#bayBody');
			if (bayBodyElement && !disableBannerOffset) {
				if (bayBodyElement.classList.contains('banner-single')) {
					setBannerType('bayfilterdrawer-banner-single');
				} else if (bayBodyElement.classList.contains('banner-double')) {
					setBannerType('bayfilterdrawer-banner-double');
				} else {
					setBannerType('bayfilterdrawer-no-banner');
				}
			} else {
				setBannerType('bayfilterdrawer-no-banner');
			}
		}
		return () => {
			window.removeEventListener('click', onClickHandler, true);
		};
	}, [showDrawer, handleCloseDrawer, disableBannerOffset]);

	return (
		<Offcanvas
			autoFocus={autoFocus}
			backdrop={backdrop}
			backdropClassName={bannerType}
			className={clsx(
				'bay-filter-drawer',
				{ 'drawer-responsive': isDynamicWidth },
				bannerType,
				className,
			)}
			container={container ? container : undefined}
			enforceFocus={enforceFocus}
			placement={placement}
			restoreFocus={restoreFocus}
			show={showDrawer}
			style={styles ? styles : {}}
			onHide={() => {
				if (showDrawer && shouldCloseDrawer) {
					handleCloseDrawer();
				}
			}}
		>
			{!hideDrawerTitle && (
				<Offcanvas.Header
					className="border-bottom p-3 px-4"
					closeButton={headerChildren ? false : true}
				>
					{title && !headerChildren ? (
						<Offcanvas.Title>{title}</Offcanvas.Title>
					) : headerChildren ? (
						<>{headerChildren}</>
					) : (
						''
					)}
				</Offcanvas.Header>
			)}
			<Offcanvas.Body>{children}</Offcanvas.Body>
			{footerChildren && (
				<div className="border-top p-3 px-4">{footerChildren}</div>
			)}
		</Offcanvas>
	);
};

export default BayFilterDrawer;
